import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, SimpleGrid, Spinner, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Button, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, HStack } from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import { useTableDensity } from '../context/TableDensityContext';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';

import ComprehensiveFilterChips from '../components/filters/ComprehensiveFilterChips';
import SummaryCard from '../components/SummaryCard';
import TrendChart from '../components/SampleBarChart';
import { useProjectContext } from '../context/ProjectContext';
import HelpTooltip from '../components/ui/HelpTooltip';
import client from '../utils/fetchClient';
import useComprehensiveFilterState from '../utils/useComprehensiveFilterState';
import FilterSelect from '../components/filters/FilterSelect';

const MetricsPage = () => {
  const { density } = useTableDensity();
  const tableSize = density === 'compact' ? 'sm' : 'md';
  
  // Use comprehensive filter state
  const { filters, debouncedFilters, updateFilter, buildApiParams } = useComprehensiveFilterState({
    period: 'daily',
    compareTo: true
  });
  
  // Local state
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [trendMetrics, setTrendMetrics] = useState([]);
  const [selectedFailure, setSelectedFailure] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { projects, effectiveProjectParam } = useProjectContext();

  // Projects come from ProjectContext

  // Fetch group/module options when project/date changes
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const params = buildApiParams();
        if (effectiveProjectParam) params.project = effectiveProjectParam;
        const res = await client.get('/api/modules', { query: params });
        if (!res.ok) throw new Error('Failed to fetch modules');
        const data = await res.json();
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        setGroups([]);
      }
    };
    fetchGroups();
  }, [effectiveProjectParam, debouncedFilters.dateRange, debouncedFilters.startDate, debouncedFilters.endDate, buildApiParams]);

  // Fetch metrics from API when filters change
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = buildApiParams();
        if (effectiveProjectParam) params.project = effectiveProjectParam;
        const res = await client.get('/api/metrics', { query: params });
        if (!res.ok) throw new Error('Failed to fetch metrics');
        const data = await res.json();
        setSummary(data.summary || null);
        setTrendMetrics(data.metrics || []);
        setPreviousSummary(data.previousSummary || null);
      } catch (err) {
        setError('Failed to fetch metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [debouncedFilters, effectiveProjectParam, buildApiParams]);

  function getTrend(current, previous, isLowerBetter = false) {
    if (previous === undefined || previous === null) return 'neutral';
    if (current === undefined || current === null) return 'neutral';
    if (current === previous) return 'neutral';
    if (isLowerBetter) {
      return current < previous ? 'up' : 'down';
    }
    return current > previous ? 'up' : 'down';
  }

  function getTrendLabel(trend, previousValue) {
    if (previousValue === undefined || previousValue === null) return 'No previous data';
    if (trend === 'up') return 'Up';
    if (trend === 'down') return 'Down';
    return 'No change';
  }

  // Map API summary to summary cards, fallback to mock data
  const safeSummary = summary || {};
  const summaryMetrics = summary
    ? [
        {
          label: 'Total Runs',
          value: safeSummary.totalRuns ?? '-',
          trend: getTrend(safeSummary.totalRuns, previousSummary?.totalRuns),
          tooltipText: 'Total number of test runs in the selected period.'
        },
        {
          label: 'Pass Rate',
          value: safeSummary.passRate !== undefined ? `${safeSummary.passRate}%` : '-',
          trend: getTrend(safeSummary.passRate, previousSummary?.passRate),
          tooltipText: 'Percentage of tests that passed.'
        },
        {
          label: 'Fail Rate',
          value: safeSummary.failureRate !== undefined ? `${safeSummary.failureRate}%` : '-',
          trend: getTrend(safeSummary.failureRate, previousSummary?.failureRate, true),
          tooltipText: 'Percentage of tests that failed.'
        },
        {
          label: 'Avg Duration',
          value: safeSummary.totalRuns && safeSummary.totalDuration !== undefined ? `${Math.round(safeSummary.totalDuration / safeSummary.totalRuns / 1000)}s` : '-',
          trend: getTrend(
            safeSummary.totalRuns && safeSummary.totalDuration !== undefined
              ? Math.round(safeSummary.totalDuration / safeSummary.totalRuns / 1000)
              : null,
            previousSummary && previousSummary.totalRuns && previousSummary.totalDuration !== undefined
              ? Math.round(previousSummary.totalDuration / previousSummary.totalRuns / 1000)
              : null,
            true
          ),
          tooltipText: 'Average duration of test runs.'
        },
        {
          label: 'Flakiness',
          value: safeSummary.flakiness !== undefined ? `${safeSummary.flakiness}%` : '-',
          trend: getTrend(safeSummary.flakiness, previousSummary?.flakiness, true),
          tooltipText: 'Percentage of tests with inconsistent results.'
        }
      ]
    : [
        {
          label: 'Total Runs',
          value: 128,
          trend: 'up',
          tooltipText: 'Total number of test runs in the selected period.'
        },
        {
          label: 'Pass Rate',
          value: '92%',
          trend: 'up',
          tooltipText: 'Percentage of tests that passed.'
        },
        {
          label: 'Fail Rate',
          value: '8%',
          trend: 'down',
          tooltipText: 'Percentage of tests that failed.'
        },
        {
          label: 'Avg Duration',
          value: '1m 23s',
          trend: 'down',
          tooltipText: 'Average duration of test runs.'
        },
        {
          label: 'Flakiness',
          value: '3.2%',
          trend: 'neutral',
          tooltipText: 'Percentage of tests with inconsistent results.'
        }
      ];

  // Map trendMetrics to chartData for the chart (use correct keys from backend)
  const chartData = (trendMetrics && trendMetrics.length > 0)
    ? trendMetrics.map(item => ({
        period: item.date,
        passed: item.passedTests,
        failed: item.failedTests,
        skipped: item.skippedTests,
      }))
    : [];
  // Debug: log chartData
  // console.debug('TrendChart data:', chartData);
  // Helper to generate a key for the chart (forces re-render)
  function getChartKey(arr) {
    return arr.map(d => `${d.period}:${d.passed}:${d.failed}:${d.skipped}`).join('|');
  }

  // Show only the module filename (strip path prefixes like "api/payroll/")
  function getModuleFilename(pathOrName) {
    if (!pathOrName || typeof pathOrName !== 'string') return pathOrName;
    const parts = pathOrName.split('/');
    return parts[parts.length - 1];
  }

  // Limit tables to top 5 records for concise overview
  const topGroupBreakdown = (summary?.groupBreakdown ?? []).slice(0, 5);
  const topRecentFailures = (summary?.recentFailures ?? []).slice(0, 5);
  
  // Prepare filter options
  const filterOptions = {
    projects: projects,
    groups: groups.map(g => g.module || g.group)
  };
  
  return (
    <Page>
      {/* Header with Filters */}
      <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="xl" px={{ base: 3, md: 4 }} py={{ base: 2, md: 3 }} mb={3}>
        <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
          <Heading size="lg" fontWeight="bold" display="flex" alignItems="center">Metrics <HelpTooltip label="Explore trends, breakdowns and failures for the selected project and time period." href="https://docs.example.com/qa/metrics" /></Heading>
          
          {/* Filters - Visible on all screen sizes */}
          <HStack spacing={3} wrap="wrap" flex="1" justify="flex-end">
            {/* Status Filter */}
            <FilterSelect
              value={filters.status}
              onChange={(value) => updateFilter('status', value)}
              options={[
                { value: 'passed', label: 'Passed' },
                { value: 'failed', label: 'Failed' },
                { value: 'skipped', label: 'Skipped' },
                { value: 'flaky', label: 'Flaky' },
              ]}
              placeholder="All Statuses"
            />
            
            {/* Group Filter */}
            {filterOptions.groups && filterOptions.groups.length > 0 && (
              <FilterSelect
                value={filters.group}
                onChange={(value) => updateFilter('group', value)}
                options={filterOptions.groups.map(g => ({ value: g, label: g }))}
                placeholder="All Groups"
              />
            )}
            
            {/* Period Filter */}
            <FilterSelect
              value={filters.period}
              onChange={(value) => updateFilter('period', value)}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
              ]}
              maxW="160px"
            />
          </HStack>
        </Flex>
      </Box>
      <Box h="1px" bg="gray.200" mb={6} />

      <ComprehensiveFilterChips 
        filters={filters} 
        updateFilter={updateFilter} 
        showDateRange={false}
        showProject={false}
        showCompareTo={false}
      />
      {/* Loading/Error States */}
      {loading && (<LoadingState message="Loading metrics" />)}
      {error && (<ErrorState message={error} onRetry={() => window.location.reload()} />)}
      {/* Summary Cards Grid */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing={6} mb={10}>
        {summaryMetrics.map((metric, idx) => (
          <SummaryCard
            key={metric.label}
            label={metric.label}
            value={metric.value}
            trend={metric.trend}
            trendLabel={getTrendLabel(metric.trend, previousSummary ? previousSummary[metric.label.replace(/\s/g, '').toLowerCase()] : null)}
            tooltipText={metric.tooltipText}
          />
        ))}
      </SimpleGrid>
      {/* Trend Chart */}
      <SectionCard mb={10}>
        <Heading size="md" mb={4}>Test Results Trend</Heading>
        <Box w="full">
          <TrendChart
            data={chartData}
            series={[
              { key: 'passed', label: 'Passed', color: '#38A169', type: 'line' },
              { key: 'failed', label: 'Failed', color: '#E53E3E', type: 'line' },
              { key: 'skipped', label: 'Skipped', color: '#ECC94B', type: 'line' },
            ]}
            xKey="period"
            height={350}
            chartType="line"
            syncId="metrics-trend"
            key={getChartKey(chartData)}
            width="100%"
          />
        </Box>
      </SectionCard>
      {/* Group/Module Breakdown Table */}
      <SectionCard mb={10}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Group/Module Breakdown</Heading>
          <Button size="sm" variant="outline" onClick={() => {
            const params = new URLSearchParams();
            if (filters.period) params.set('period', filters.period);
            if (filters.startDate) params.set('start', filters.startDate);
            if (filters.endDate) params.set('end', filters.endDate);
            window.location.href = `/test-case-groups?${params.toString()}`;
          }}>View all groups</Button>
        </Flex>
        {loading ? (
          <Flex align="center" justify="center" minH="120px">
            <Spinner size="lg" color="green.400" />
          </Flex>
        ) : summary && Array.isArray(summary.groupBreakdown) && summary.groupBreakdown.length === 0 ? (
          <Text color="gray.500">No breakdown data available for this period.</Text>
        ) : (
          <>
          <TableContainer w="100%" overflowX="auto" display={{ base: 'none', md: 'block' }}>
            <Table size={tableSize} variant="striped" sx={{ th: { position: 'sticky', top: 0, bg: 'white', zIndex: 1 } }}>
              <Thead>
                <Tr>
                  <Th>Group/Module</Th>
                  <Th isNumeric>Runs</Th>
                  <Th isNumeric>Pass Rate</Th>
                  <Th isNumeric>Fail Rate</Th>
                  <Th isNumeric>Avg Duration</Th>
                  <Th isNumeric>Flakiness</Th>
                </Tr>
              </Thead>
              <Tbody>
                {topGroupBreakdown.map((row, idx) => (
                  <Tr key={row.group || idx}>
                    <Td fontWeight="semibold">{getModuleFilename(row.group)}</Td>
                    <Td isNumeric>{row.runs}</Td>
                    <Td isNumeric color="green.600">{row.passRate !== undefined ? `${Math.round(row.passRate)}%` : '-'}</Td>
                    <Td isNumeric color="red.600">{row.failRate !== undefined ? `${Math.round(row.failRate)}%` : '-'}</Td>
                    <Td isNumeric>{row.avgDuration !== undefined ? `${Math.round(row.avgDuration / 1000)}s` : '-'}</Td>
                    <Td isNumeric color="yellow.600">{row.flakiness !== undefined ? `${Math.round(row.flakiness * 10) / 10}%` : '-'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {/* Mobile stacked cards */}
          <Box display={{ base: 'block', md: 'none' }}>
            {topGroupBreakdown.map((row, idx) => (
              <Box
                key={row.group || idx}
                borderWidth={1}
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
                mb={3}
                bg="white"
                overflow="hidden"
                wordBreak="break-word"
                sx={{ overflowWrap: 'anywhere' }}
              >
                <Text color="gray.500">Group/Module</Text>
                <Text fontWeight="semibold" mb={2} whiteSpace="normal">
                  {getModuleFilename(row.group)}
                </Text>
                <Flex justify="space-between" mb={1}><Text color="gray.500">Runs</Text><Text>{row.runs}</Text></Flex>
                <Flex justify="space-between" mb={1}><Text color="gray.500">Pass Rate</Text><Text color="green.600">{row.passRate !== undefined ? `${Math.round(row.passRate)}%` : '-'}</Text></Flex>
                <Flex justify="space-between" mb={1}><Text color="gray.500">Fail Rate</Text><Text color="red.600">{row.failRate !== undefined ? `${Math.round(row.failRate)}%` : '-'}</Text></Flex>
                <Flex justify="space-between" mb={1}><Text color="gray.500">Avg Duration</Text><Text>{row.avgDuration !== undefined ? `${Math.round(row.avgDuration / 1000)}s` : '-'}</Text></Flex>
                <Flex justify="space-between"><Text color="gray.500">Flakiness</Text><Text color="yellow.600">{row.flakiness !== undefined ? `${Math.round(row.flakiness * 10) / 10}%` : '-'}</Text></Flex>
              </Box>
            ))}
          </Box>
          </>
        )}
      </SectionCard>
      {/* Recent Failures Table */}
      <SectionCard mb={10}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Recent Failures</Heading>
          <Button size="sm" variant="outline" onClick={() => {
            const params = new URLSearchParams();
            if (filters.period) params.set('period', filters.period);
            if (filters.startDate) params.set('start', filters.startDate);
            if (filters.endDate) params.set('end', filters.endDate);
            window.location.href = `/test-runs?${params.toString()}`;
          }}>View all failures</Button>
        </Flex>
        {loading ? (
          <Flex align="center" justify="center" minH="120px">
            <Spinner size="lg" color="red.400" />
          </Flex>
        ) : summary && Array.isArray(summary.recentFailures) && summary.recentFailures.length === 0 ? (
          <Text color="gray.500">No recent failures for this period. 🎉</Text>
        ) : (
          <>
          <TableContainer w="100%" overflowX="auto" display={{ base: 'none', md: 'block' }}>
            <Table size={tableSize} variant="striped" sx={{ th: { position: 'sticky', top: 0, bg: 'white', zIndex: 1 } }}>
              <Thead>
                <Tr>
                  <Th>Date/Time</Th>
                  <Th>Test Name</Th>
                  <Th>Group/Module</Th>
                  <Th>Error Message</Th>
                  <Th>View</Th>
                </Tr>
              </Thead>
              <Tbody>
                {topRecentFailures.map((fail, idx) => (
                  <Tr key={fail.id || idx}>
                    <Td>{fail.date ? new Date(fail.date).toLocaleString() : '-'}</Td>
                    <Td fontWeight="semibold">{fail.testName}</Td>
                    <Td>{fail.group}</Td>
                    <Td maxW="220px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{fail.error}</Td>
                    <Td>
                      <Button size="sm" colorScheme="blue" variant="outline" onClick={() => { setSelectedFailure(fail); onOpen(); }}>
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {/* Mobile stacked cards */}
          <Box display={{ base: 'block', md: 'none' }}>
            {topRecentFailures.map((fail, idx) => (
              <Box
                key={fail.id || idx}
                borderWidth={1}
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
                mb={3}
                bg="white"
                overflow="hidden"
                wordBreak="break-word"
                sx={{ overflowWrap: 'anywhere' }}
              >
                <Flex justify="space-between" mb={1}><Text color="gray.500">Date/Time</Text><Text maxW="60%" textAlign="right">{fail.date ? new Date(fail.date).toLocaleString() : '-'}</Text></Flex>
                <Text color="gray.500">Test Name</Text>
                <Text fontWeight="semibold" mb={1} whiteSpace="normal">{fail.testName}</Text>
                <Text color="gray.500">Group/Module</Text>
                <Text mb={1} whiteSpace="normal">{fail.group}</Text>
                <Box mt={2}>
                  <Text color="gray.500" fontSize="sm" mb={1}>Error</Text>
                  <Text fontSize="sm" color="red.600" whiteSpace="normal">{fail.error}</Text>
                </Box>
              </Box>
            ))}
          </Box>
          </>
        )}
      </SectionCard>
      {/* Modal for failure details */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Failure Details</ModalHeader>
          <ModalBody>
            {selectedFailure && (
              <Box>
                <Text mb={2}><b>Test Name:</b> {selectedFailure.testName}</Text>
                <Text mb={2}><b>Group/Module:</b> {selectedFailure.group}</Text>
                <Text mb={2}><b>Date/Time:</b> {selectedFailure.date ? new Date(selectedFailure.date).toLocaleString() : '-'}</Text>
                <Text mb={2}><b>Error Message:</b></Text>
                <Box bg="gray.100" borderRadius="md" p={3} fontFamily="mono" fontSize="sm" whiteSpace="pre-wrap">
                  {selectedFailure.error}
                </Box>
                {/* Add more fields if available, e.g., runId, stack trace, etc. */}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} colorScheme="green">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Page>
  );
};

export default MetricsPage; 